import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import i18n from '@/lib/i18n'
import { useAuth } from '@/auth/AuthProvider'
import { DiaryService } from '@/lib/firestore/diary'
import { writeDailySummary } from '@/lib/firestore/summaries'
import { updateStreak, checkStreakStatus, refundFreezesForDates } from '@/lib/firestore/streak'
import { computeSummary } from '@/diary/summary'
import { dayScoreInputsForWrite, storedDayScoreInputs } from '@/diary/today-score'
import { dayPerformance } from '@/diary/day-band'
import { entryHasAlcohol, fluidEntryHasNutrients } from '@/lib/alcohol'
import { todayStr, addDays } from '@/lib/dates'
import type {
  DiaryEntry,
  DiaryEntryInput,
  DailySummary,
  FluidType,
  MealTime,
} from '@/types'

interface DiaryContextValue {
  selectedDate: string
  entries: DiaryEntry[]
  summary: DailySummary | null
  loading: boolean
  setSelectedDate: (d: string) => void
  goToToday: () => void
  goToPreviousDay: () => void
  goToNextDay: () => void
  canGoToNextDay: boolean
  addEntry: (input: DiaryEntryInput) => Promise<string | null>
  /** Batch add (saved-foods multi-select): one write, side effects once — mobile addEntries. */
  addEntries: (inputs: DiaryEntryInput[]) => Promise<string[]>
  /** Re-save another date's dailySummaries doc from Firestore truth (mobile parity). */
  recomputeSummaryForDate: (date: string) => Promise<void>
  addFluid: (ml: number, type?: FluidType) => Promise<string | null>
  updateEntry: (id: string, partial: Partial<DiaryEntry>) => Promise<void>
  deleteEntry: (id: string) => Promise<void>
  copyDay: (source: string, target: string, includeFluids?: boolean) => Promise<number>
  copyEntry: (entry: DiaryEntry, targetDate: string, targetMealType?: MealTime) => Promise<string>
  copyEntries: (entriesToCopy: DiaryEntry[], targetDate: string) => Promise<number>
  getEntriesByMealType: (m: MealTime) => DiaryEntry[]
  getFluidEntries: () => DiaryEntry[]
  // One-time "streak freeze used" notice — a streak-update call just bridged
  // a missed day instead of resetting. Diary shows a small inline banner.
  freezeNotice: boolean
  dismissFreezeNotice: () => void
}

const DiaryContext = createContext<DiaryContextValue | null>(null)

export function DiaryProvider({ children }: { children: ReactNode }) {
  const { user, userProfile } = useAuth()
  const [selectedDate, setSelectedDate] = useState<string>(todayStr())
  const [entries, setEntries] = useState<DiaryEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [subFailed, setSubFailed] = useState(false)
  const [freezeNotice, setFreezeNotice] = useState(false)

  // Fire-and-forget streak update that also surfaces the one-time freeze
  // notice. Kept non-blocking (same as before) so callers don't wait on it.
  // Then — never at the same time, both rewrite streakData — the freeze
  // refund for the dates that now hold real entries (mobile: updateStreak,
  // then StreakService.refundFreezesForDates).
  const updateStreakAndNotify = useCallback((uid: string, entryDate: string | undefined, refundDates: string[]) => {
    void updateStreak(uid, entryDate)
      .then((result) => {
        if (result.freezeConsumed) setFreezeNotice(true)
      })
      .catch(() => {})
      .then(() => refundFreezesForDates(uid, refundDates))
      .catch(() => {})
  }, [])

  // Real-time entries for the selected day.
  useEffect(() => {
    if (!user) {
      setEntries([])
      return
    }
    // Clear immediately so a date switch never shows (or persists) the prior day's data.
    setEntries([])
    setLoading(true)
    setSubFailed(false)
    const unsub = DiaryService.subscribeToEntriesForDate(
      user.uid,
      selectedDate,
      (next) => {
        setEntries(next)
        setSubFailed(false)
        setLoading(false)
      },
      () => {
        // On subscription error, entries is still the reset [] — mark the day
        // failed so the summary writer never clobbers the doc with zeros.
        setSubFailed(true)
        setLoading(false)
      },
    )
    return unsub
  }, [user, selectedDate])

  const summary = useMemo(
    () => computeSummary(selectedDate, entries, userProfile?.healthProfile),
    [selectedDate, entries, userProfile],
  )

  // Debounced persist of the day's rollup (week-strip dots + mobile parity).
  const writeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!user || !summary) return
    // Don't persist while the day's snapshot is still loading (or errored) —
    // `entries` may be empty/stale, which would clobber the shared
    // dailySummaries doc with bad totals.
    if (loading || subFailed) return
    // Never materialize summaries for future days (mobile can't navigate
    // there and would never create them).
    if (selectedDate > todayStr()) return
    if (writeTimer.current) clearTimeout(writeTimer.current)
    writeTimer.current = setTimeout(() => {
      const perf = dayPerformance(summary, entries)
      // Mobile DiaryContext writes the day-score inputs on the same doc; a past
      // day keeps the ceilings it was first saved with.
      const dayScore = dayScoreInputsForWrite(storedDayScoreInputs(summary, entries), selectedDate, todayStr())
      void writeDailySummary(user.uid, summary, perf, entries.length, dayScore).catch(() => {})
    }, 600)
    return () => {
      if (writeTimer.current) clearTimeout(writeTimer.current)
    }
  }, [user, summary, entries.length, loading, subFailed, selectedDate])

  // On load, zero a stale streak (mobile checkStreakStatus parity).
  useEffect(() => {
    if (!user) return
    void checkStreakStatus(user.uid).catch(() => {})
  }, [user])

  /**
   * Recompute + persist the dailySummaries doc for an arbitrary date — mobile
   * recomputeSummaryForDate. The live writer above only covers selectedDate,
   * so an add onto another day must refresh that day's doc (and its week-strip
   * dot) here or it stays stale.
   */
  const recomputeSummaryForDate = useCallback(
    async (date: string): Promise<void> => {
      if (!user || !date) return
      try {
        const dateEntries = await DiaryService.getEntriesForDate(user.uid, date)
        const daySummary = computeSummary(date, dateEntries, userProfile?.healthProfile)
        if (!daySummary) return
        const perf = dayPerformance(daySummary, dateEntries)
        const dayScore = dayScoreInputsForWrite(storedDayScoreInputs(daySummary, dateEntries), date, todayStr())
        await writeDailySummary(user.uid, daySummary, perf, dateEntries.length, dayScore)
      } catch (err) {
        console.warn('Failed to recompute summary for date:', date, err)
      }
    },
    [user, userProfile],
  )

  const addEntry = useCallback(
    async (input: DiaryEntryInput): Promise<string | null> => {
      if (!user) return null
      const entryDate = input.date || selectedDate
      const id = await DiaryService.createEntry(user.uid, {
        ...input,
        date: entryDate,
      })
      // A back-dated (or forward-dated) add: the live writer only covers the
      // day on screen (mobile useMealAnalysis → recomputeSummaryForDate).
      if (entryDate !== selectedDate) void recomputeSummaryForDate(entryDate)
      // Mobile updates the streak after every diary entry — keep them in lockstep.
      // Backfilling a frozen day refunds the freeze (mobile refundFreezesForDates).
      updateStreakAndNotify(user.uid, entryDate, [entryDate])
      return id
    },
    [user, selectedDate, updateStreakAndNotify, recomputeSummaryForDate],
  )

  const addEntries = useCallback(
    async (inputs: DiaryEntryInput[]): Promise<string[]> => {
      if (!user || inputs.length === 0) return []
      const withDates = inputs.map((input) => ({ ...input, date: input.date || selectedDate }))
      const ids = await DiaryService.createEntries(user.uid, withDates)
      // One streak update for the batch, crediting the latest entry date, then
      // the freeze refund for every batch date (mobile addEntries).
      const dates = [...new Set(withDates.map((e) => e.date))]
      const latest = dates.reduce((max, d) => (d > max ? d : max), dates[0])
      updateStreakAndNotify(user.uid, latest, dates)
      dates.filter((d) => d !== selectedDate).forEach((d) => void recomputeSummaryForDate(d))
      return ids
    },
    [user, selectedDate, updateStreakAndNotify, recomputeSummaryForDate],
  )

  const addFluid = useCallback(
    (ml: number, type: FluidType = 'water') =>
      addEntry({
        date: selectedDate,
        mealType: 'snack',
        name: i18n.t('diary.fluids.water', 'Water'),
        source: 'fluid',
        servingSize: ml,
        servingUnit: 'ml',
        servingsConsumed: 1,
        // All 11 canonical keys explicitly 0 (mobile water-log parity — the
        // shared doc shape must not depend on which client wrote it).
        nutrients: {
          calories: 0, addedSugarsG: 0, sugarsG: 0, carbsG: 0, fiberG: 0, fatG: 0,
          saturatedFatG: 0, transFatG: 0, sodiumMg: 0, proteinG: 0, cholesterolMg: 0,
        },
        isFluid: true,
        fluidMl: ml,
        fluidType: type,
        safetyLevel: 'safe',
      }),
    [addEntry, selectedDate],
  )

  const value: DiaryContextValue = {
    selectedDate,
    entries,
    summary,
    loading,
    setSelectedDate,
    goToToday: () => setSelectedDate(todayStr()),
    goToPreviousDay: () => setSelectedDate((d) => addDays(d, -1)),
    // Future days are navigable (meal planning) — mobile parity.
    goToNextDay: () => setSelectedDate((d) => addDays(d, 1)),
    canGoToNextDay: true,
    addEntry,
    addEntries,
    recomputeSummaryForDate,
    addFluid,
    updateEntry: (id, partial) => DiaryService.updateEntry(id, partial),
    deleteEntry: (id) => DiaryService.deleteEntry(id),
    // Copies credit the target day's streak and refund its freeze
    // (mobile copyDayEntries / copyEntry / copyEntries).
    copyDay: async (source, target, includeFluids) => {
      if (!user) return 0
      const count = await DiaryService.copyEntriesForDate(user.uid, source, target, includeFluids)
      // Mobile updates the streak when a copy actually logged something.
      if (count > 0) updateStreakAndNotify(user.uid, target, [target])
      return count
    },
    copyEntry: async (entry, targetDate, targetMealType) => {
      const id = await DiaryService.copySingleEntry(entry, targetDate, targetMealType)
      if (user) updateStreakAndNotify(user.uid, targetDate, [targetDate])
      return id
    },
    copyEntries: async (entriesToCopy, targetDate) => {
      const n = await DiaryService.copyMultipleEntries(entriesToCopy, targetDate)
      if (user && n > 0) updateStreakAndNotify(user.uid, targetDate, [targetDate])
      return n
    },
    getEntriesByMealType: (m) =>
      entries.filter(
        (e) =>
          e.mealType === m &&
          // Keep nutritive entries; hide pure-fluid logs (they live in the
          // fluid tracker) — mobile utils/fluids.ts thresholds.
          !(e.isFluid && !fluidEntryHasNutrients(e)),
      ),
    // Alcoholic drinks never render as hydration pills (mobile parity).
    getFluidEntries: () => entries.filter((e) => e.isFluid && !entryHasAlcohol(e)),
    freezeNotice,
    dismissFreezeNotice: () => setFreezeNotice(false),
  }

  return <DiaryContext.Provider value={value}>{children}</DiaryContext.Provider>
}

export function useDiary(): DiaryContextValue {
  const ctx = useContext(DiaryContext)
  if (!ctx) throw new Error('useDiary must be used within DiaryProvider')
  return ctx
}
